import { ReactElement, useEffect, useState } from 'react';
import { Tab, Tabs } from '@flos/react-ui';
import track, { errorTypes } from 'c9-tracking';

import { TEndpointAPIConfig, useConfigContext } from 'context/ConfigContext';

import { getGiftCardsByType } from 'common/async/async-calls';
import { productList } from 'common/helpers/product';

import ActiveGiftCardTable from './ActiveGiftCardTable/ActiveGiftCardTable';
import ExpiredGiftCardTable from './ExpiredGiftCardTable/ExpiredGiftCardTable';

/* ==================================== Local Constants ==================================== */
const PRODUCTS_TO_FETCH = Object.values(productList)
  .filter((product) => product.shouldShow)
  .map((product) => product.value);

/* ==================================== Expired Types ==================================== */
export type TGiftCard = {
  id: string;
  product: string;
  productLabel: string;
  amount: number;
  startDate: number;
  endDate: number;
  campaign: string;
};

/* ==================================== Local Types ==================================== */
type TUseTableDetails = (getAllGiftCardsofTypeEndpoint: TEndpointAPIConfig['getAllGiftCardsofType']) => {
  isLoading: boolean;
  activeGiftCards: Array<TGiftCard>;
  expiredGiftCards: Array<TGiftCard>;
};

type THomePage = () => ReactElement;

/* ==================================== Local Functions ==================================== */
const useTableDetails: TUseTableDetails = (getAllGiftCardsofTypeEndpoint) => {
  const [isLoading, setIsLoading] = useState(true);
  const [[activeGiftCards, expiredGiftCards], setGiftCards] = useState<[Array<TGiftCard>, Array<TGiftCard>]>([[], []]);

  useEffect(() => {
    setIsLoading(true);

    Promise.all(PRODUCTS_TO_FETCH.map((product) => getGiftCardsByType(getAllGiftCardsofTypeEndpoint, product)))
      .then((apiResults) => {
        const giftCards = apiResults.reduce((result: Array<TGiftCard>, apiResult: any) => {
          apiResult.data.message.forEach((record: any) => {
            const productLabel = productList[record.key as keyof typeof productList];

            if (record.key === productLabel.value) {
              result.push({
                id: String(record.id),
                amount: record.amount,
                product: productLabel.value,
                productLabel: productLabel.label,
                startDate: record.startDate,
                endDate: record.endDate,
                campaign: record.campaign
              });
            }
          });

          return result;
        }, [] as Array<TGiftCard>);

        if (giftCards?.length > 0) {
          const [activeGiftCards, expiredGiftCards] = giftCards.reduce(
            (result: [Array<TGiftCard>, Array<TGiftCard>], giftCard: TGiftCard) => {
              const currentDate = new Date().getTime();

              if (giftCard.endDate >= currentDate) {
                result[0].push(giftCard);
              } else {
                result[1].push(giftCard);
              }

              return result;
            },
            [[], []]
          );

          setGiftCards([activeGiftCards, expiredGiftCards]);
        }
      })
      .catch(() => {
        track.fault('Failed to call product API', errorTypes.tech, 'Get Giftcard');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [getAllGiftCardsofTypeEndpoint]);

  return { isLoading, activeGiftCards, expiredGiftCards };
};

/* ==================================== Main Component ==================================== */
const HomePage: THomePage = () => {
  const {
    endpointAPI: { getAllGiftCardsofType: getAllGiftCardsofTypeEndpoint }
  } = useConfigContext();

  const { isLoading, activeGiftCards, expiredGiftCards } = useTableDetails(getAllGiftCardsofTypeEndpoint);

  return (
    <Tabs>
      <Tab label="Liste over Gavekort">
        <ActiveGiftCardTable isLoading={isLoading} giftCards={activeGiftCards} />
      </Tab>
      <Tab label="Udløbne Gavekort">
        <ExpiredGiftCardTable isLoading={isLoading} giftCards={expiredGiftCards} />
      </Tab>
    </Tabs>
  );
};

export default HomePage;
